import { serverApi } from '../index';

// types
import {
  Company,
  CompanyBuildingsCount,
  CompanyPropertiesCount,
  CompanyTopGallonsUnits,
  CompanyType,
  CompanyUnitsCount,
  Threshold
} from 'types/company';
import { PaginatedReq, PaginatedRes } from 'types/pagination';
import { Sensor, SensorByEntityFilter } from 'types/sensor';
import { KeySetPaginationReq, KeySetPaginationRes } from 'types/key-set-pagination';
import { Parsed } from 'types/submission';
import { AlertsOverview, Notification, OpenLeak } from 'types/notification';
import { Property } from 'types/property';
import { TimeIntervalEnum } from 'types/time-interval.enum';
import { Building } from 'types/building';
import { NotificationItem } from 'types/notification';
import { Timezone, WaterVolumeType } from 'types/location';
import { Invoice } from 'types/savings';
import { CityBill } from 'types/city-bill';

// tools
import { getClientTimezone } from 'utils/helper';

export const companyApi = serverApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<PaginatedRes<Company>, PaginatedReq | void>({
      query: (arg) => {
        const { filter, ...others } = arg || {};
        return {
          url: `/companies`,
          params: {
            ...others,
            ...filter
          }
        };
      },
      providesTags: (result) => (result ? [...result.data.map(({ id }) => ({ type: 'Company' as const, id })), 'Company'] : ['Company'])
    }),
    getTopCompanies: builder.query<Company[], string>({
      query: (name) => ({ url: `/companies/search-top/${encodeURIComponent(name)}/5` }),
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: 'Company' as const, id })), 'Company'] : ['Company'])
    }),
    getCompaniesName: builder.query<
      Company[],
      { excluded?: '0' | '1'; excludedIds?: number[]; sort?: 'ASC' | 'DESC'; company_type_id?: number[] } | void
    >({
      query: ({ ...params } = {}) => ({
        url: `/companies/names`,
        params
      }),
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: 'Company' as const, id })), 'Company'] : ['Company'])
    }),
    getCompany: builder.query<Company, number>({
      query: (id) => {
        return `/companies/${id}`;
      },
      providesTags: (result, error, id) => [{ type: 'Company', id }]
    }),
    getCompanyPorpertiesCount: builder.query<CompanyPropertiesCount, number>({
      query: (id) => `/companies/${id}/properties-count`
    }),
    removeCompany: builder.mutation<void, number>({
      query: (id) => ({ url: `/companies/${id}`, method: 'delete' })
    }),
    createCompany: builder.mutation<void, Omit<Company, 'id' | 'created' | 'modified' | 'image_url' | 'expose_savings'>>({
      query: (body) => ({ url: `/companies`, method: 'post', body }),
      invalidatesTags: ['Company']
    }),
    addPropertyToBillingCompany: builder.mutation<void, { company_id: number; property_id: number }>({
      query: ({ company_id, property_id }) => ({
        url: `/companies/${company_id}/billing-company-properties/${property_id}`,
        method: 'post'
      }),
      invalidatesTags: ['Property']
    }),
    removePropertyFromBillingCompany: builder.mutation<void, { company_id: number; property_id: number }>({
      query: ({ company_id, property_id }) => ({
        url: `/companies/${company_id}/billing-company-properties/${property_id}`,
        method: 'delete'
      }),
      invalidatesTags: ['Property']
    }),
    updateCompany: builder.mutation<void, Omit<Company, 'created' | 'modified' | 'image_url' | 'expose_savings'>>({
      query: (data) => {
        const { id, ...body } = data;
        return { url: `/companies/${id}`, method: 'put', body };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Company', id }]
    }),
    getCompanyStatistics: builder.query<Record<string, any>, number>({ query: (id) => `/companies/${id}/statistics` }),
    getCompanyTypes: builder.query<CompanyType[], void>({ query: () => `/companies/types` }),
    getSensorsByCompany: builder.query<PaginatedRes<Sensor>, SensorByEntityFilter & PaginatedReq>({
      query: ({ id, ...arg }) => {
        const { filter, ...others } = arg;
        return {
          url: `/companies/${id}/sensors`,
          params: {
            ...others,
            ...filter
          }
        };
      },
      providesTags: (result) => (result ? [...result.data.map(({ id }) => ({ type: 'Sensor' as const, id })), 'Sensor'] : ['Sensor'])
    }),
    getSubmissionsByCompany: builder.query<KeySetPaginationRes<Parsed>, { id: number } & KeySetPaginationReq>({
      query: ({ id, ...arg }) => {
        return { url: `/companies/${id}/submissions`, params: arg || undefined };
      },
      providesTags: (result) =>
        result ? [...result.docs.map(({ _id }) => ({ type: 'ParsedSubmission' as const, _id })), 'ParsedSubmission'] : ['ParsedSubmission']
    }),
    getSubmissionsByCompanyCount: builder.query<{ total_count: number }, { filter?: KeySetPaginationReq['filter']; id: number }>({
      query: ({ id, ...arg }) => {
        return { url: `/companies/${id}/submissions/count`, params: arg || undefined };
      }
    }),
    getAlertsByCompany: builder.query<KeySetPaginationRes<Notification>, { id: number } & KeySetPaginationReq>({
      query: ({ id, ...arg }) => {
        return { url: `/companies/${id}/alerts`, params: arg || undefined };
      },
      providesTags: (result) =>
        result ? [...result.docs.map(({ _id }) => ({ type: 'Notification' as const, _id })), 'Notification'] : ['Notification']
    }),
    getAlertsByCompanyCount: builder.query<{ total_count: number }, { id: number; filter: KeySetPaginationReq['filter'] }>({
      query: ({ id, ...arg }) => {
        return { url: `/companies/${id}/alerts/count`, params: arg || undefined };
      },
      providesTags: ['Notification']
    }),
    getCompanyOpenAlertsCount: builder.query<{ company_id: number; open_alerts_count: number }, number>({
      query: (id) => `/companies/${id}/open-alerts-count`,
      providesTags: ['Notification']
    }),
    getCompanyGallonsByAppliance: builder.query<{ appliance: string; gallons: number }[], { id: number; from: number; to: number }>({
      query: ({ id, from, to }) => `/companies/${id}/appliance-gallons?from=${from}&to=${to}`
    }),
    getCompanyGallons: builder.query<
      { date: string; items: [{ id: number; name: string; gallons: number }] }[],
      { id: number; from: number; to: number; ptpd: number; timeInterval: TimeIntervalEnum; propertyIds?: number[] }
    >({
      query: ({ id, ...args }) => ({ url: `/companies/${id}/gallons`, params: args })
    }),
    getCompanyAppliancesThresholds: builder.query<PaginatedRes<Threshold>, { id: number } & PaginatedReq>({
      query: ({ id, ...arg }) => {
        const { filter, ...others } = arg || {};
        return {
          url: `/companies/${id}/appliances-thresholds`,
          params: { ...others, ...filter }
        };
      },
      providesTags: (result) =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'CompanyApplianceThreshold' as const, id })), 'CompanyApplianceThreshold']
          : ['CompanyApplianceThreshold']
    }),
    updateCompanyApplianceThreshold: builder.mutation<void, Pick<Threshold, 'company_id' | 'appliance_type_id' | 'threshold' | 'id'>>({
      query: (data) => {
        const { company_id, appliance_type_id, threshold } = data;
        return { url: `/companies/${company_id}/appliances-thresholds/${appliance_type_id}/${threshold}`, method: 'put' };
      },
      invalidatesTags: (_, __, { id }) => [{ type: 'CompanyApplianceThreshold', id }]
    }),
    getCompanyPropertiesNames: builder.query<Property[], { id: number; limit?: number; sort?: 'ASC' | 'DESC' }>({
      query: ({ id, limit, ...params }) => {
        const limitPath = limit ? `/${limit}` : '';
        return { url: `/companies/${id}/properties-names${limitPath}`, params };
      },
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: 'Property' as const, id })), 'Property'] : ['Property'])
    }),
    getTopPropertiesByGallonsUsage: builder.query<
      { id: number; gallons: number }[],
      { id: number; period?: 'current-month' | 'last-year' }
    >({
      query: ({ id, ...params }) => {
        return { url: `/companies/${id}/top-gallons-properties`, params };
      },
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: 'Property' as const, id })), 'Property'] : ['Property'])
    }),
    getCompanyProperties: builder.query<PaginatedRes<Property>, { id: number } & PaginatedReq>({
      query: ({ id, ...arg }) => {
        const { filter, ...others } = arg || {};
        return {
          url: `/companies/${id}/properties`,
          params: {
            ...others,
            ...filter
          }
        };
      },
      providesTags: (result) => (result ? [...result.data.map(({ id }) => ({ type: 'Property' as const, id })), 'Property'] : ['Property'])
    }),
    getCompanyCityBills: builder.query<PaginatedRes<CityBill>, { id: number } & PaginatedReq>({
      query: ({ id, ...arg }) => {
        const { filter, ...others } = arg || {};
        return {
          url: `/companies/${id}/city-bills`,
          params: {
            ...others,
            ...filter
          }
        };
      },
      providesTags: (result) => (result ? [...result.data.map(({ id }) => ({ type: 'CityBill' as const, id })), 'CityBill'] : ['CityBill'])
    }),
    getSubmissionsGallonsCountByCompany: builder.query<{ gallons_count: number }, { id: number } & KeySetPaginationReq>({
      query: ({ id, ...arg }) => {
        return { url: `/companies/${id}/submissions-gallons-count`, params: arg || undefined };
      },
      providesTags: ['ParsedSubmission']
    }),
    getCompanyBuildingsCount: builder.query<CompanyBuildingsCount, number>({
      query: (id) => `/companies/${id}/buildings-count`
    }),
    getCompanyUnitsCount: builder.query<CompanyUnitsCount, number>({
      query: (id) => `/companies/${id}/units-count`
    }),
    getCompanyTotalUnitCount: builder.query<CompanyUnitsCount, number>({
      query: (id) => `/companies/${id}/total-unit-count`
    }),
    getCompanyWaterVolumeType: builder.query<WaterVolumeType, number>({
      query: (id) => `/companies/get-water-volume-type/${id}`
    }),
    getCompanyResidentialUnitsCount: builder.query<number, number>({
      query: (id) => `/companies/${id}/residential-units`
    }),
    getCompanyFormattedOpenLeaks: builder.query<OpenLeak[], { id: number }>({
      query: ({ id }) => `/companies/${id}/open-leaks`,
      providesTags: ['Notification']
    }),
    getCompanyTopProperties: builder.query<Property[], { company_id: number; name: string; excluded?: number[] }>({
      query: ({ company_id, name, ...arg }) => ({
        url: `/companies/${company_id}/search-top-properties/${encodeURIComponent(name)}/5`,
        params: arg
      }),
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: 'Property' as const, id })), 'Property'] : ['Property'])
    }),
    getCompanyTopBuildings: builder.query<Building[], { company_id: number; name: string }>({
      query: ({ company_id, name }) => ({
        url: `/companies/${company_id}/search-top-buildings/${encodeURIComponent(name)}/5`
      }),
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: 'Building' as const, id })), 'Building'] : ['Building'])
    }),
    getCompanyAlertsOverview: builder.query<AlertsOverview, { id: number }>({
      query: ({ id }) => `/companies/${id}/alerts-overview`,
      providesTags: ['Notification']
    }),
    getCompanyReportOverview: builder.query<NotificationItem[], { id: number; report: number }>({
      query: ({ id, report }) => `/companies/${id}/report-overview/${report}`,
      providesTags: ['Notification']
    }),
    getCompanyTopGallonsUnits: builder.query<CompanyTopGallonsUnits[], { id: number }>({
      query: ({ id }) => ({ url: `/companies/${id}/top-gallons-units?timeZone=${getClientTimezone()}` })
    }),
    getCompanyTimeZone: builder.query<Timezone, number>({
      query: (id) => ({ url: `/companies/${id}/timezone` })
    }),
    getCompanyChartAlerts: builder.query<
      { date: string; items: [{ name: string; alerts: number }] }[],
      {
        id: number;
        from: number;
        to: number;
        propertyIds?: number[];
        resolved?: boolean;
      }
    >({
      query: ({ id, ...args }) => ({ url: `/companies/${id}/dashboard-chart-alerts`, params: args })
    }),
    getCompanyInvoices: builder.query<PaginatedRes<Invoice>, { id: number } & PaginatedReq>({
      query: ({ id, filter, ...others }) => {
        return { url: `/companies/${id}/invoices`, params: { ...filter, ...others } };
      }
    }),
    updateCreateAlertCostThreshold: builder.mutation<Company, { company_id: number; cost?: number | null }>({
      query: (data) => {
        const { company_id, ...body } = data;
        return { url: `/companies/${company_id}/update-create-alert-cost-threshold`, method: 'put', body };
      },
      invalidatesTags: (_, __, { company_id }) => [{ type: 'Company', company_id }]
    }),
    updateExposeSavings: builder.mutation<void, { id: number; value: 0 | 1 }>({
      query: ({ id, value }) => ({
        url: `/companies/${id}/expose-savings/${value}`,
        method: 'put'
      })
    })
  }),
  overrideExisting: false
});

export const {
  useGetCompaniesQuery,
  useGetCompanyPorpertiesCountQuery,
  useRemoveCompanyMutation,
  useCreateCompanyMutation,
  useLazyGetCompanyQuery,
  useUpdateCompanyMutation,
  useGetCompaniesNameQuery,
  useGetCompanyQuery,
  useGetCompanyStatisticsQuery,
  useGetSensorsByCompanyQuery,
  useGetSubmissionsByCompanyQuery,
  useGetCompanyOpenAlertsCountQuery,
  useGetAlertsByCompanyQuery,
  useGetCompanyGallonsByApplianceQuery,
  useGetCompanyAppliancesThresholdsQuery,
  useUpdateCompanyApplianceThresholdMutation,
  useLazyGetCompanyPropertiesNamesQuery,
  useGetCompanyPropertiesQuery,
  useLazyGetCompanyOpenAlertsCountQuery,
  useLazyGetCompaniesNameQuery,
  useGetSubmissionsGallonsCountByCompanyQuery,
  useGetSubmissionsByCompanyCountQuery,
  useLazyGetTopCompaniesQuery,
  useGetCompanyBuildingsCountQuery,
  useGetCompanyUnitsCountQuery,
  useLazyGetCompanyGallonsQuery,
  useGetCompanyResidentialUnitsCountQuery,
  useGetAlertsByCompanyCountQuery,
  useGetCompanyPropertiesNamesQuery,
  useGetCompanyFormattedOpenLeaksQuery,
  useLazyGetCompanyTopBuildingsQuery,
  useLazyGetCompanyTopPropertiesQuery,
  useGetCompanyAlertsOverviewQuery,
  useGetCompanyReportOverviewQuery,
  useGetCompanyTopGallonsUnitsQuery,
  useLazyGetCompanyChartAlertsQuery,
  useAddPropertyToBillingCompanyMutation,
  useRemovePropertyFromBillingCompanyMutation,
  useGetCompanyTypesQuery,
  useGetTopPropertiesByGallonsUsageQuery,
  useLazyGetCompanyTimeZoneQuery,
  useLazyGetTopPropertiesByGallonsUsageQuery,
  useLazyGetCompanyInvoicesQuery,
  useLazyGetCompanyCityBillsQuery,
  useGetCompanyInvoicesQuery,
  useGetCompanyCityBillsQuery,
  useGetCompanyWaterVolumeTypeQuery,
  useGetCompanyTotalUnitCountQuery,
  useUpdateCreateAlertCostThresholdMutation,
  useUpdateExposeSavingsMutation
} = companyApi;
